// src/context/LocationContext.js
import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';

// Tiempo máximo de espera para la geolocalización (ms)
const GEOLOCATION_TIMEOUT_MS = 1000000;

// Coordenadas por defecto (p. ej. centro de tu ciudad)
export const DEFAULT_COORDS = { lat: 40.4170128, lng: -3.703529 };

export const LocationContext = createContext({
  coords: DEFAULT_COORDS,
  loading: true,
  clearLocation: () => {},
  overrideLocation: () => {}
});

export function LocationProvider({ children }) {
  // 2) ¿Tenemos coords guardadas? Si sí, las usamos; si no, null indica “pendiente”
  const [coords, setCoords]   = useState(() => {
    const stored = localStorage.getItem('petludos_coords');
    return stored ? JSON.parse(stored) : null;
  });
  // 3) ¿Ya preguntamos en esta sesión?
  const [asked, setAsked]     = useState(() =>
    localStorage.getItem('petludos_loc_asked') === 'true'
  );
  // 4) Estado de “estamos esperando permiso”
  //    Si ya preguntamos, loading arranca en false y nunca parpadea
  const [loading, setLoading] = useState(!asked);
  // Ref para almacenar el ID del timeout
  const fallbackTimerRef      = useRef(null);

  // —————————————————————————————————————————————————————
  // Manejo del fallback cuando pasa el timeout
  const handleFallback = useCallback(() => {
    console.log(
      '⚠️ Geolocalización: fallback tras timeout o denegación, usando coords por defecto:',
      DEFAULT_COORDS
    );
    setCoords(DEFAULT_COORDS);
    localStorage.setItem('petludos_coords', JSON.stringify(DEFAULT_COORDS));
    setLoading(false);
  }, []);

  // —————————————————————————————————————————————————————
  // Éxito de geolocalización
  const success = useCallback(({ coords: { latitude, longitude } }) => {
    clearTimeout(fallbackTimerRef.current);
    const c = { lat: latitude, lng: longitude };
    console.log('✅ Geolocalización exitosa, coordenadas reales:', c);
    setCoords(c);
    localStorage.setItem('petludos_coords', JSON.stringify(c));
    setLoading(false);
  }, []);

  // Error o denegación
  const fail = useCallback((err) => {
    clearTimeout(fallbackTimerRef.current);
    console.warn('❌ Geolocalización denegada o error:', err);
    handleFallback();
  }, [handleFallback]);

  // —————————————————————————————————————————————————————
  // Función para solicitar geolocalización,
  // con la opción de programar o no el timeout de fallback
  const requestGeo = useCallback((useFallback = true) => {
    // Limpio cualquier timer previo
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
    }
    // Si es la primera llamada, programo el timeout
    if (useFallback) {
      fallbackTimerRef.current = setTimeout(
        handleFallback,
        GEOLOCATION_TIMEOUT_MS
      );
    }
    navigator.geolocation.getCurrentPosition(success, fail);
  }, [handleFallback, success, fail]);

  // —————————————————————————————————————————————————————
  // Primer efecto: preguntar permisos y pedir geolocalización con fallback
  useEffect(() => {
    if (asked) return;  // solo la primera vez

    localStorage.setItem('petludos_loc_asked', 'true');
    setAsked(true);

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((status) => {
          if (status.state === 'denied') {
            console.log('🔒 Permiso de geolocalización denegado de entrada');
            handleFallback();
          } else {
            // Primer request con timeout
            requestGeo(true);
          }
        })
        .catch(() => {
          requestGeo(true);
        });
    } else {
      // navegadores antiguos
      requestGeo(true);
    }

    // Cleanup: limpiar el timer si se desmonta
    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
    };
  }, [asked, handleFallback, requestGeo]);

  // —————————————————————————————————————————————————————
  // Segundo efecto: suscribirse a cambios posteriores en el permiso
  useEffect(() => {
    if (!navigator.permissions || !navigator.permissions.query) return;

    let permissionStatus;
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((status) => {
        permissionStatus = status;
        status.onchange = () => {
          console.log('🔄 Cambio en permiso de geolocalización:', status.state);
          if (status.state === 'granted') {
            // Reintentamos sin fallback para no programar otro timeout
            console.log('🔄 Permiso concedido tras fallback, reintentando sin fallback');
            requestGeo(false);
          }
        };
      })
      .catch(() => {});

    return () => {
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, [requestGeo]);

  // —————————————————————————————————————————————————————
  // Log de coords cada vez que cambien
  useEffect(() => {
    if (!loading) {
      console.log('📍 Ubicación activa en contexto:', coords);
    }
  }, [coords, loading]);

  // Limpia hasta coordenadas por defecto (p.ej. al pulsar “Limpiar ubicación”)
  const clearLocation = () => {
    console.log('🔄 clearLocation invocado, reseteando a coords por defecto:', DEFAULT_COORDS);
    setCoords(DEFAULT_COORDS);
    localStorage.setItem('petludos_coords', JSON.stringify(DEFAULT_COORDS));
  };

  // Permite al usuario sobreescribir coords (p.ej. desde ModalLocation)
  const overrideLocation = (newCoords) => {
    console.log('🔄 overrideLocation invocado, nueva ubicación:', newCoords);
    setCoords(newCoords);
    localStorage.setItem('petludos_coords', JSON.stringify(newCoords));
  };

  return (
    <LocationContext.Provider
      value={{
        coords: coords || DEFAULT_COORDS,
        loading,
        clearLocation,
        overrideLocation
      }}
    >
      {children}
    </LocationContext.Provider>
  );
}
